import { Link } from "@inertiajs/react"
import { ChevronRight, MapPin, UserPlus, X } from "lucide-react"
import { useState } from "react"

import { POSITION_LABELS, type PositionValue } from "@/lib/positions"
import { typedPost } from "@/lib/typed-router"
import type { FollowUser } from "@/types/router-mutations"

interface SuggestedUser {
  id: number
  first_name: string
  last_name: string
  full_name: string
  nickname: string
  role: string
  avatar_url: string | null
  followers_count: number
  position: string | null
  country: string | null
  city: string | null
  mutual_count: number
  profile_slug: string | null
}

const ROLE_LABELS: Record<string, string> = {
  player: "Jugador",
  scout: "Scout",
  club: "Club",
}

function profilePath(user: SuggestedUser): string {
  const slug = user.profile_slug
  if (!slug) return "#"
  if (user.role === "scout") return `/scouts/${slug}`
  if (user.role === "club") return `/clubes/${slug}`
  return `/jugadores/${slug}`
}

export default function FollowRecommendations({
  suggestions,
}: {
  suggestions: SuggestedUser[]
}) {
  const [followedIds, setFollowedIds] = useState<Set<number>>(new Set())
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem("dismissed_suggestions")
      return stored ? new Set(JSON.parse(stored) as number[]) : new Set()
    } catch {
      return new Set()
    }
  })

  const visibleSuggestions = suggestions.filter((s) => !dismissedIds.has(s.id))

  if (visibleSuggestions.length === 0) return null

  const handleFollow = (userId: number) => {
    setFollowedIds((prev) => new Set([...prev, userId]))
    typedPost<FollowUser>(
      `/users/${userId}/follows`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        onError: () => {
          setFollowedIds((prev) => {
            const next = new Set(prev)
            next.delete(userId)
            return next
          })
        },
      },
    )
  }

  const handleDismiss = (userId: number) => {
    setDismissedIds((prev) => {
      const next = new Set([...prev, userId])
      try {
        localStorage.setItem("dismissed_suggestions", JSON.stringify([...next]))
      } catch {
        // ignore storage errors
      }
      return next
    })
  }

  return (
    <div className="border-border bg-card my-4 rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus className="text-krown-100 h-4 w-4" />
          <h3 className="text-sm font-bold text-white">
            Personas que quizás conozcas
          </h3>
        </div>
        <Link
          href="/buscar"
          className="text-krown-100 flex items-center gap-0.5 text-xs font-medium transition-colors hover:text-white"
        >
          Ver más
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-2">
        {visibleSuggestions.map((user) => {
          const isFollowed = followedIds.has(user.id)
          const initials = `${user.first_name[0]}${user.last_name[0]}`
          const positionLabel = user.position
            ? POSITION_LABELS[user.position as PositionValue]
            : null
          const location = [user.city, user.country].filter(Boolean).join(", ")

          return (
            <div
              key={user.id}
              className="border-border bg-background relative max-w-[170px] min-w-[150px] flex-shrink-0 rounded-lg border p-3 text-center"
            >
              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(user.id)}
                className="text-krown-grey-50 absolute top-1.5 right-1.5 rounded-full p-0.5 transition-colors hover:text-white"
                aria-label="Descartar"
              >
                <X className="h-3.5 w-3.5" />
              </button>

              {/* Avatar */}
              <Link href={profilePath(user)} className="inline-block">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.full_name}
                    className="mx-auto mb-2 h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="bg-krown-200 mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold text-white">
                    {initials}
                  </div>
                )}
              </Link>

              {/* Name */}
              <Link
                href={profilePath(user)}
                className="hover:text-krown-100 block truncate text-sm font-semibold text-white"
              >
                {user.full_name}
              </Link>

              {/* Role badge */}
              <span className="bg-krown-200/20 text-krown-100 mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium">
                {ROLE_LABELS[user.role] ?? user.role}
              </span>

              {/* Position (players only) */}
              {positionLabel && (
                <p className="text-muted-foreground mt-1 truncate text-xs">
                  {positionLabel}
                </p>
              )}

              {/* Location */}
              {location && (
                <div className="text-krown-grey-50 mt-1 flex items-center justify-center gap-0.5 text-xs">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{location}</span>
                </div>
              )}

              {/* Mutual connections */}
              {user.mutual_count > 0 && (
                <p className="text-muted-foreground mt-1 text-[10px]">
                  {user.mutual_count} seguidores en común
                </p>
              )}

              {/* Follow button */}
              <button
                onClick={() => !isFollowed && handleFollow(user.id)}
                className={`mt-2 w-full rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  isFollowed
                    ? "border-krown-100 text-krown-100 border"
                    : "bg-krown-100 text-krown-800 hover:bg-krown-50"
                }`}
              >
                {isFollowed ? "Siguiendo" : "Seguir"}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
